import { useAboutUs } from '@/hooks/useQueries';
import { createFileRoute } from '@tanstack/react-router';
import React, { useMemo, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { API_CONFIG } from '@/lib/config';
import { SubSection } from '@/lib/types';
import { Spinner } from '@opaz/ui-components';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@/hooks/useAnalytics';
import handshake from '@/assets/handshake.png';

export const Route = createFileRoute('/about-us/')({
  component: AboutusPage,
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: 'breadcrumbs.aboutUs',
      }),
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'About Us - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

const SubsectionRow: React.FC<{
  section: SubSection;
  bgColor: string;
}> = React.memo(({ section, bgColor }) => (
  <article className="md:table-row flex flex-col" role="group" aria-labelledby={`section-title-${section.id}`}>
    <div
      className={`md:table-cell md:pr-16 md:align-middle md:rounded-s md:rounded-e-none rounded-t ps-7 pe-7 py-4 ${bgColor}`}
    >
      <div className="flex justify-start md:justify-center items-baseline">
        <h2 id={`section-title-${section.id}`} className="text-5xl font-bold text-primary-dark-green">
          {section.number}
        </h2>
        <h1 className="text-5xl ms-1 text-primary-light-green capitalize" aria-hidden="true">
          {section.unit}
        </h1>
      </div>
      <p
        className="text-textColor-primary text-base font-normal text-start md:text-center md:whitespace-nowrap"
        aria-label={`Metric summary: ${section.shortText}`}
      >
        {section.shortText}
      </p>
    </div>

    <div
      className={`md:table-cell md:align-middle md:rounded-e md:rounded-s-none rounded-b ps-7 pe-7 py-4 w-full ${bgColor}`}
    >
      <p className="text-black" aria-label={`Description: ${section.description}`}>
        {section.description}
      </p>
    </div>
  </article>
));

export const SubsectionTable: React.FC<{ sections: SubSection[] }> = ({ sections }) => {
  const backgroundColors = useMemo(() => ['bg-info', 'bg-info-success', 'bg-info-warning'], []);

  return (
    <section aria-labelledby="subsection-table-heading" className="flex w-full flex-col">
      <h2 id="subsection-table-heading" className="sr-only">
        Statistics and Highlights
      </h2>
      <div className="flex justify-between flex-col gap-5 flex-1 h-full">
        {sections.map((section, index) => (
          <SubsectionRow
            key={section.id || index}
            section={section}
            bgColor={backgroundColors[index % backgroundColors.length]}
          />
        ))}
      </div>
    </section>
  );
};

export default function AboutusPage() {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { data: aboutUsData, isLoading: aboutUsLoading, isError, isLoadingError } = useAboutUs();

  const subsections = useMemo(() => {
    if (!aboutUsData?.data?.subSection) return [];
    return [...aboutUsData.data.subSection].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [aboutUsData]);

  // Track performance issues
  useEffect(() => {
    if ((isError || isLoadingError) && analytics) {
      analytics.trackApiError('about-us', 500);
    }
  }, [isError, isLoadingError, analytics]);

  if (isError || isLoadingError) {
    return (
      <div role="alert" className="text-center text-red-500">
        Error loading data
      </div>
    );
  }

  if (aboutUsLoading) {
    return (
      <div className="flex justify-center items-center py-20" role="status" aria-live="polite">
        <div className="flex flex-col items-center justify-center h-full">
          <Spinner />
          <p className="text-primary-green mt-4">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="w-full border-b border-white px-4 md:py-10">
        <div className="mx-auto mt-6 max-w-container-960 pb-5 md:mt-32 md:pb-8">
          <div className="mb-6 space-y-3 text-center md:mb-10 md:space-y-4">
            <h1 className="text-3xl md:text-6xl font-bold text-primary-dark-green">{aboutUsData?.data?.Title}</h1>
            <p className="text-xl text-primary-green">{aboutUsData?.data?.subTitle}</p>
          </div>

          <figure className="relative h-[260px] w-full sm:h-[400px] md:h-[588px]">
            <video
              ref={videoRef}
              className="rounded-3xl w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-label="Company introduction video"
              title="About Us Video"
            >
              <source
                src={`${API_CONFIG.STRAPI_BASE_URL.replace('/api', '')}${aboutUsData?.data?.video?.url}`}
                type="video/webm"
              />
              {t('common.videoFallbackMessage')}
            </video>
          </figure>
        </div>
      </header>

      <main className="w-full px-4 lg:px-0">
        <div className="mx-auto max-w-container-960 pb-10 md:pb-20">
          <div className="flex flex-col gap-5 lg:flex-row justify-between">
            <figure className="max-w-[417px] overflow-hidden">
              <img
                src={handshake}
                alt="Two people shaking hands"
                className="rounded-3xl w-full object-cover"
                loading="lazy"
              />
              <figcaption className="sr-only">Partnership illustration</figcaption>
            </figure>

            <div className="flex justify-between flex-1">
              {subsections.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <Spinner className="mx-auto" />
                  <p className="text-primary-green mt-4 text-center">{t('common.noDataAvailable')}</p>
                </div>
              ) : (
                <SubsectionTable sections={subsections} />
              )}
            </div>
          </div>
        </div>
      </main>

      <section className="w-full bg-muted-bg px-8 md:px-0" aria-labelledby="aboutus-final-title">
        <div className="mx-auto max-w-container-960 space-y-5 py-10 md:space-y-7 md:py-20">
          <h2 id="aboutus-final-title" className="text-3xl text-primary-dark-green">
            {aboutUsData?.data?.lastTitle}
          </h2>
          <div
            className="text-xl text-black"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(aboutUsData?.data?.bodyContent || '') }}
            aria-label="Detailed company description"
          ></div>
        </div>
      </section>
    </>
  );
}
